import defaultTheme from "@acin.reactjs/acin-ui/theme";
import "@fontsource/rubik/latin.css";
import "@fontsource/source-sans-3/latin.css";
import merge from "lodash/merge";
import { DefaultTheme } from "styled-components";
import { PartialDeep } from "type-fest";
import BackOfficeSkeletonTheme from "./components/BackOfficeSkeletonTheme";
import barProgressTheme from "./components/BarProgressTheme";
import { AppBreakpoints } from "./components/BreakpointsTheme";
import ButtonsTheme from "./components/ButtonsTheme";
import callMeTheme from "./components/CallMeTheme";
import checkboxTheme from "./components/CheckboxTheme";
import CircularProgressTheme from "./components/CircularProgressTheme";
import cookiesBarTheme from "./components/CookiesBarTheme";
import discountCodeTheme from "./components/DiscountCodeTheme";
import dropdownTheme from "./components/DropdownTheme";
import EmptyStateTheme from "./components/EmptyStateTheme";
import errorTheme from "./components/ErrorTheme";
import FileUploaderTheme from "./components/FileUploaderTheme";
import FormReadOnlyTheme from "./components/FormReadOnlyTheme";
import GridTheme from "./components/GridTheme";
import IconButtonTheme from "./components/IconButtonsTheme";
import inputTheme from "./components/InputTheme";
import languageSelectorTheme from "./components/LanguageSelectorTheme";
import loadingTheme from "./components/LoadingTheme";
import LoginTheme from "./components/LoginTheme";
import menuListTheme from "./components/MenuListTheme";
import notificationBarsTheme from "./components/NotificationBarsTheme";
import NotificationsTheme from "./components/NotificationsTheme";
import overlayTheme from "./components/OverlayTheme";
import { PaletteTheme } from "./components/PaletteTheme";
import radioTheme from "./components/RadioTheme";
import SearchBarTheme from "./components/SearchBarTheme";
import selectTheme from "./components/SelectTheme";
import SideBarTheme from "./components/SideBarTheme";
import SidePaneTheme from "./components/SidePaneTheme";
import StepsTheme from "./components/StepsTheme";
import SwitchButtonTheme from "./components/SwitchButtonTheme";
import SwitchTheme from "./components/SwitchTheme";
import TableTheme from "./components/TableTheme";
import TabsTheme from "./components/TabsTheme";
import TypographyTheme from "./components/TypographyTheme";

export const appBaseTheme: PartialDeep<DefaultTheme> = {
  palette: PaletteTheme,
  breakpoints: AppBreakpoints,
  elevation: {
    xs: { boxShadow: "0 1px 3px rgba(0, 0, 0, 0.16)" },
    sm: { boxShadow: "0 2px 6px rgba(0, 0, 0, 0.16)" },
    md: { boxShadow: "0 6px 12px rgba(0, 0, 0, 0.16)" },
    lg: { boxShadow: "0 8px 20px rgba(0, 0, 0, 0.16)" },
  },
  radius: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    rounded: 50,
  },
  typography: TypographyTheme,
};

const appTheme = merge({}, defaultTheme, appBaseTheme) as DefaultTheme;

export const resolveComponentsTheme = (
  theme: DefaultTheme,
): PartialDeep<Pick<DefaultTheme, "components">> => ({
  components: {
    backofficeSkeleton: BackOfficeSkeletonTheme(theme),
    grid: GridTheme(theme),
    button: ButtonsTheme(theme),
    sideBar: SideBarTheme(theme),
    formReadOnly: FormReadOnlyTheme(theme),
    switch: SwitchTheme(theme),
    tabs: TabsTheme(theme),
    checkbox: checkboxTheme(theme),
    iconButton: IconButtonTheme(theme),
    menuList: menuListTheme(theme),
    languageSelector: languageSelectorTheme,
    switchButton: SwitchButtonTheme(theme),
    dropdown: dropdownTheme,
    input: inputTheme(theme),
    barProgress: barProgressTheme(theme),
    sidePane: SidePaneTheme(),
    notifications: NotificationsTheme(theme),
    searchBar: SearchBarTheme(theme),
    steps: StepsTheme(theme),
    circularProgress: CircularProgressTheme(theme),
    table: TableTheme(theme),
    cookiesBar: cookiesBarTheme,
    overlay: overlayTheme,
    error: errorTheme(theme),
    login: LoginTheme(),
    callMe: callMeTheme(theme),
    notificationBar: notificationBarsTheme,
    fileUploader: FileUploaderTheme,
    emptyState: EmptyStateTheme,
    select: selectTheme(),
    radio: radioTheme,
    discountCode: discountCodeTheme,
    loading: loadingTheme(),
  },
});

export default appTheme;
